/**
 * Progress pride card — stage / classes / exams + download & earn CTA.
 */
import { APP_DOWNLOAD_URL } from "./shareCopy";
import { canvasToPngFile, shareNative, type NativeShareResult } from "./nativeShare";
import { viralShareFooter } from "./shareCopy";
import { MAX_CURRICULUM_STAGE } from "@piano-tutor/shared";

export interface ShareProgressInput {
  studentName: string;
  unlockedStage: number;
  classesCompleted: number;
  classesTotal: number;
  examsPassed: number;
  examsTotal: number;
  currentClassTitle?: string;
}

export function renderShareProgressCard(input: ShareProgressInput): HTMLCanvasElement {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#031022");
  bg.addColorStop(0.5, "#0a2a58");
  bg.addColorStop(1, "#031022");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = "#8ad6ff";
  ctx.font = "700 26px Oxanium, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ULTIMATE PIANO TUTOR", W / 2, 110);
  ctx.fillStyle = "#ffd56a";
  ctx.font = "800 40px Oxanium, system-ui, sans-serif";
  ctx.fillText("MY PROGRESS", W / 2, 175);

  const name = (input.studentName || "Piano Student").trim().slice(0, 28);
  ctx.fillStyle = "#fff6e8";
  ctx.font = "700 52px Oxanium, system-ui, sans-serif";
  ctx.fillText(name, W / 2, 270);

  const rows: Array<[string, string]> = [
    ["Stage", `${input.unlockedStage} / ${MAX_CURRICULUM_STAGE}`],
    ["Classes cleared", `${input.classesCompleted} / ${input.classesTotal}`],
    ["Gatekeepers", `${input.examsPassed} / ${input.examsTotal}`],
  ];
  if (input.currentClassTitle) {
    rows.push(["Focus class", input.currentClassTitle.slice(0, 36)]);
  }

  rows.forEach(([label, value], i) => {
    const y = 360 + i * 110;
    ctx.fillStyle = "rgba(10,42,88,0.92)";
    roundRect(ctx, 120, y, W - 240, 90, 16);
    ctx.fill();
    ctx.strokeStyle = "rgba(62,182,255,0.4)";
    ctx.stroke();
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(242,248,255,0.65)";
    ctx.font = "600 22px Sora, system-ui, sans-serif";
    ctx.fillText(label, 160, y + 35);
    ctx.fillStyle = "#ffd56a";
    ctx.font = "800 34px Oxanium, system-ui, sans-serif";
    ctx.fillText(value, 160, y + 72);
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "#f2f8ff";
  ctx.font = "600 24px Sora, system-ui, sans-serif";
  ctx.fillText("Learn classical piano easily — free download", W / 2, 900);
  ctx.fillStyle = "#4dffb0";
  ctx.font = "700 22px Oxanium, system-ui, sans-serif";
  ctx.fillText("Share & Earn · invite friends · earn when they buy", W / 2, 945);
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "600 20px Oxanium, system-ui, sans-serif";
  ctx.fillText("Android: Google Play  ·  Windows: Microsoft Store", W / 2, 990);

  ctx.fillStyle = "rgba(242,248,255,0.4)";
  ctx.font = "500 18px Sora, system-ui, sans-serif";
  ctx.fillText("Android · Windows  ·  LOTI Games and Apps", W / 2, 1260);
  return canvas;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function buildProgressShareText(input: ShareProgressInput): string {
  const name = input.studentName.trim() || "I";
  return (
    `${name}'s Ultimate Piano Tutor progress:\n` +
    `Stage ${input.unlockedStage}/${MAX_CURRICULUM_STAGE} · Classes ${input.classesCompleted}/${input.classesTotal} · Exams ${input.examsPassed}/${input.examsTotal}\n` +
    (input.currentClassTitle ? `Now on: ${input.currentClassTitle}\n` : "") +
    `\n` +
    viralShareFooter()
  );
}

export async function shareProgressCard(input: ShareProgressInput): Promise<NativeShareResult> {
  const canvas = renderShareProgressCard(input);
  const file = await canvasToPngFile(canvas, "upt-progress.png");
  return shareNative({
    title: "My Piano Progress",
    text: buildProgressShareText(input),
    url: APP_DOWNLOAD_URL,
    file,
    downloadName: "upt-progress.png",
  });
}
